import { Box, Tabs } from "@mantine/core";
import { IconFiles, IconMessage } from "@tabler/icons-react";
import { useMemo } from "react";
import { default as ReactMarkdown } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { Reference } from "src/db";
import type { PluggableList } from "unified";
import {
    extractReferencedIdsWithNumbers,
    processMarkdownContent,
} from "../utils/markdownProcessing";
import { InlineReference } from "./InlineReference";
import type { ComponentsWithInlineRef, InlineRefProps } from "./MessageItem";
import { MDA, MDCode, MDTable } from "./markdown/MarkdownComponents";
import { PdfFocusProvider } from "./PDFViewer/context";
import { ReferenceViewer } from "./ReferenceViewer";

interface AnswerWithSourcesProps {
    answer: string;
    references?: Reference[];
    children?: React.ReactNode;
    className?: string;
    dataTestId?: string;
    loading?: boolean;
    error?: boolean;
    customContent?: React.ReactNode;
    showDisclaimer?: boolean;
}

export const AnswerWithSources = ({
    answer,
    references,
    children,
    className = "markdown",
    dataTestId = "markdown",
    loading = false,
    error = false,
    customContent,
    showDisclaimer = false,
}: AnswerWithSourcesProps) => {
    const referencedIdsWithNumbers = useMemo(
        () => extractReferencedIdsWithNumbers(answer || ""),
        [answer],
    );

    const validReferencesCount = useMemo(() => {
        return referencedIdsWithNumbers.filter((refInfo) =>
            references?.find((ref) => ref.reference_id === refInfo.id),
        ).length;
    }, [referencedIdsWithNumbers, references]);

    const processedReferences = useMemo(() => {
        return referencedIdsWithNumbers
            .map((refInfo) => {
                const reference = references?.find((ref) => ref.reference_id === refInfo.id);
                return reference ? { ...reference, index: refInfo.displayNumber } : null;
            })
            .filter((ref) => ref !== null)
            .sort((a, b) => {
                const orderA =
                    referencedIdsWithNumbers.find((r) => r.id === a.reference_id)?.order || 0;
                const orderB =
                    referencedIdsWithNumbers.find((r) => r.id === b.reference_id)?.order || 0;
                return orderA - orderB;
            });
    }, [referencedIdsWithNumbers, references]);

    const additionalReferences = useMemo(() => {
        const mentionedIds = new Set(referencedIdsWithNumbers.map((r) => r.id));
        return references?.filter((ref) => !mentionedIds.has(ref.reference_id));
    }, [referencedIdsWithNumbers, references]);

    const markdownComponents = useMemo(
        () =>
            ({
                table: MDTable,
                code: MDCode,
                a: MDA,
                "inline-ref": ({ node, ...props }: InlineRefProps) => {
                    const { index, display } = props;
                    const refItem = references?.find((ref) => ref.reference_id === index);

                    if (refItem) {
                        return (
                            <InlineReference
                                reference={refItem}
                                index={display || index}
                                showDisclaimer={showDisclaimer}
                            />
                        );
                    }
                    return <span>[{display || index}]</span>;
                },
            }) as ComponentsWithInlineRef,
        [references, showDisclaimer],
    );

    if (!references || references.length === 0) {
        return (
            <Box className={className} data-testid={dataTestId}>
                <PdfFocusProvider>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw] as PluggableList}
                        components={{
                            table: MDTable,
                            code: MDCode,
                            a: MDA,
                        }}
                    >
                        {answer}
                    </ReactMarkdown>
                </PdfFocusProvider>
            </Box>
        );
    }

    return (
        <Tabs defaultValue="answer" variant="outline">
            <Tabs.List mb="md">
                <Tabs.Tab value="answer" leftSection={<IconMessage size={14} />}>
                    Answer
                </Tabs.Tab>
                <Tabs.Tab
                    data-testid="sources-tab"
                    value="sources"
                    leftSection={<IconFiles size={14} />}
                >
                    Sources ({validReferencesCount})
                </Tabs.Tab>
                {children}
            </Tabs.List>

            <Tabs.Panel value="answer" pt="md">
                <Box className={className} data-testid={dataTestId}>
                    {loading
                        ? customContent
                        : !error && (
                              <PdfFocusProvider>
                                  <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[rehypeRaw] as PluggableList}
                                      components={markdownComponents}
                                  >
                                      {processMarkdownContent(answer, references)}
                                  </ReactMarkdown>
                              </PdfFocusProvider>
                          )}
                    {customContent}
                </Box>
            </Tabs.Panel>

            <Tabs.Panel value="sources" pt="md">
                <ReferenceViewer
                    references={processedReferences}
                    additionalReferences={additionalReferences}
                    showDisclaimer={showDisclaimer}
                />
            </Tabs.Panel>
        </Tabs>
    );
};
